import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
} from 'react'
import { deleteRaster, getAllRasters } from '../../../../api/endpoints'
import { useAuth } from '../../../../Contexts/AuthContext'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { useLidarUpload } from '../../../../hooks/useLidarUpload'
import { toast } from '../../../../lib/toast'
import { useTheme } from '../../../../theme/hooks'
import { getFileExtension } from '../lidarModalUtils'
import { DateField } from './DateField'
import { FileDropzone } from './FileDropzone'
import { Footer } from './Footer'
import { ImportProgressPanel } from './ImportProgressPanel'
import { RastersTable } from './RastersTable'
import { ConfirmModal } from '../../../shared/index'
import type { ModalLidarProps, Mode, Raster, StoredUser } from './types'

const ALLOWED_EXTENSIONS = ['.las', '.laz'] as const
const FILE_ACCEPT = ALLOWED_EXTENSIONS.join(',')
const FILENAME_PATTERN = /^[a-zA-Z0-9 -]{1,50}$/

const generateUploadId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

const readStoredUser = (): StoredUser | null => {
  try {
    const raw = window.localStorage.getItem('user')
    return raw ? (JSON.parse(raw) as StoredUser) : null
  } catch (err) {
    console.warn('[lidar-modal] failed to read user from storage', err)
    return null
  }
}

const formatLocalDateForFilename = (date: Date): string =>
  date.toLocaleDateString().split('/').join('-')

const ModalLidar = ({
  isOpen,
  onUploadSuccess,
  onClose,
  mode = 'desktop',
}: ModalLidarProps) => {
  const { theme } = useTheme()
  const { hasPermission } = useAuth()
  const isMobileViewport = useIsMobile()
  const effectiveMode: Mode = isMobileViewport ? 'mobile' : mode
  // Meme droit que la suppression de couche et de dossier du panneau Couches de
  // donnees, et que la route back (`requirePermission('lidar:upload')`).
  const canDeleteRaster = hasPermission('lidar:upload')

  const {
    progressState,
    uploadQueue,
    effectiveUploading,
    isQueueFull,
    maxQueue,
    enqueueUpload,
    removeFromQueue,
    cancelCurrent,
  } = useLidarUpload()

  const [fileName, setFileName] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedDateString, setSelectedDateString] = useState('')
  const [user, setUser] = useState<StoredUser | null>(null)
  const [rasters, setRasters] = useState<Raster[]>([])

  const bodyOverflowRef = useRef<string | null>(null)

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchRasters = useCallback(async (resortId?: string) => {
    if (!resortId) return
    try {
      const response = await getAllRasters(resortId)
      setRasters(Array.isArray(response) ? (response as Raster[]) : [])
    } catch (error) {
      console.error('[lidar-modal] fetchRasters failed', error)
    }
  }, [])

  useEffect(() => {
    const stored = readStoredUser()
    setUser(stored)
    void fetchRasters(stored?.user?.resort_id)
  }, [fetchRasters])

  // ── Body overflow lock while modal is visible ──────────────────────────────
  useEffect(() => {
    if (isOpen) {
      bodyOverflowRef.current = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = bodyOverflowRef.current ?? ''
        bodyOverflowRef.current = null
      }
    }
    if (bodyOverflowRef.current !== null) {
      document.body.style.overflow = bodyOverflowRef.current
      bodyOverflowRef.current = null
    }
    return undefined
  }, [isOpen])

  // ── Form lifecycle ─────────────────────────────────────────────────────────
  const resetFormState = useCallback(() => {
    setFileName('')
    setSelectedFile(null)
    setSelectedDate(new Date())
    setSelectedDateString('')
  }, [])

  const handleCloseModal = useCallback(() => {
    resetFormState()
    onClose?.()
  }, [onClose, resetFormState])

  const handleAnimationComplete = useCallback(() => {
    if (!isOpen) onClose?.()
  }, [isOpen, onClose])

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file)
    setFileName(file.name)
  }, [])

  const handleDateChange = useCallback((date: Date, iso: string) => {
    setSelectedDate(date)
    setSelectedDateString(iso)
  }, [])

  const handleSubmit = useCallback(
    (event?: FormEvent | MouseEvent) => {
      event?.preventDefault()

      if (!selectedDate) {
        toast.error('Veuillez sélectionner une date.')
        return
      }
      if (!fileName) {
        toast.error('Veuillez saisir un nom de fichier.')
        return
      }
      if (!selectedFile) {
        toast.error('Veuillez choisir un fichier.')
        return
      }
      const extension = getFileExtension(selectedFile.name)
      if (
        !ALLOWED_EXTENSIONS.includes(
          extension as (typeof ALLOWED_EXTENSIONS)[number],
        )
      ) {
        toast.error(
          `Format non autorisé. Formats acceptés: ${ALLOWED_EXTENSIONS.join(', ')}`,
        )
        return
      }
      if (!FILENAME_PATTERN.test(fileName)) {
        toast.error(
          'Le nom du fichier doit contenir uniquement des lettres, des chiffres et des tirets, et avoir une longueur maximale de 20 caractères.',
        )
        return
      }
      if (isQueueFull) {
        toast.error(`File d’attente pleine (max ${maxQueue}).`)
        return
      }

      const stored = readStoredUser()
      const clientId = stored?.user?.id
      const resortId = stored?.user?.resort_id
      const userId = stored?.user?.id
      if (!clientId || !resortId || !userId) {
        toast.error('Utilisateur non identifié, veuillez vous reconnecter.')
        return
      }

      const uploadId = generateUploadId()
      const updatedFileName = `${fileName}_${formatLocalDateForFilename(selectedDate)}`
      const formData = new FormData()
      formData.append('resort_id', resortId)
      formData.append('filename', updatedFileName)
      formData.append('url', selectedFile)
      formData.append('date', selectedDate.toISOString())
      formData.append('user_id', userId)
      formData.append('client_id', clientId)
      formData.append('upload_id', uploadId)

      const accepted = enqueueUpload({
        formData,
        clientId,
        uploadId,
        fileName: updatedFileName,
        onUploadSuccess,
      })

      if (!accepted) {
        toast.error(`File d’attente pleine (max ${maxQueue}).`)
        return
      }

      resetFormState()
      toast.success('Ajouté à la file d’attente')
    },
    [
      enqueueUpload,
      fileName,
      isQueueFull,
      maxQueue,
      onUploadSuccess,
      resetFormState,
      selectedDate,
      selectedFile,
    ],
  )

  // Confirmation en etat local et non via la cle `confirmModal` du ModalContext :
  // DataLayers rend son propre ConfirmModal sur cette cle et les deux composants
  // sont montes ensemble, on afficherait deux confirmations superposees.
  const [rasterToDelete, setRasterToDelete] = useState<Raster | null>(null)

  const confirmDeleteRaster = useCallback(async () => {
    if (!rasterToDelete) return
    setRasterToDelete(null)
    try {
      await deleteRaster(rasterToDelete.id)
      toast.success('Raster supprimé avec succès')
      await fetchRasters(user?.user?.resort_id)
    } catch (error) {
      console.error('[lidar-modal] deleteRaster failed', error)
      toast.error('Erreur lors de la suppression du raster')
    }
  }, [fetchRasters, rasterToDelete, user?.user?.resort_id])

  const handleCancel = useCallback(() => {
    void cancelCurrent()
  }, [cancelCurrent])

  const variants = useMemo(() => {
    const isMobile = effectiveMode === 'mobile'
    return {
      initial: isMobile
        ? { opacity: 0, y: '100%' }
        : { opacity: 0, scale: 0.9, y: 20 },
      animate: isMobile ? { opacity: 1, y: 0 } : { opacity: 1, scale: 1, y: 0 },
      exit: isMobile
        ? { opacity: 0, y: '100%' }
        : { opacity: 0, scale: 0.9, y: 20 },
      transition: isMobile
        ? { type: 'spring', stiffness: 400, damping: 35 }
        : {
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
          },
    }
  }, [effectiveMode])

  const isMobile = effectiveMode === 'mobile'

  return (
    <AnimatePresence mode="wait" onExitComplete={handleAnimationComplete}>
      {isOpen && (
        <motion.div
          key="lidar-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={isMobile ? handleCloseModal : undefined}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            backgroundColor: theme.modal.overlay,
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
          }}
        >
          <motion.div
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={variants.transition}
            onClick={isMobile ? e => e.stopPropagation() : undefined}
            className="modal-box flex flex-col overflow-hidden p-0"
            style={{
              maxHeight: isMobile ? '80vh' : '90vh',
              borderRadius: isMobile ? '16px 16px 0 0' : '8px',
              maxWidth: isMobile ? '100%' : '32rem',
              width: '100%',
              position: 'relative',
              margin: isMobile ? 0 : 'auto',
              backgroundColor: theme.modal.bg,
              color: theme.text.primary,
              border: isMobile ? 'none' : `1px solid ${theme.section.border}`,
              boxShadow: theme.modal.shadow,
            }}
          >
            {isMobile && (
              <div
                className="flex justify-center pb-2 pt-3"
                style={{ backgroundColor: theme.surface.primary }}
              >
                <div
                  className="h-1 w-10 rounded-full"
                  style={{ backgroundColor: theme.border.primary }}
                />
              </div>
            )}

            <div
              className="sticky top-0 z-10 flex items-center justify-between px-6 py-5"
              style={{
                borderBottom: `1px solid ${theme.section.border}`,
                backgroundColor: theme.surface.primary,
              }}
            >
              <div>
                <h2
                  className="text-lg font-medium"
                  style={{ color: theme.text.primary }}
                >
                  Importer les données LiDAR
                </h2>
                <p
                  className="mt-1 text-sm"
                  style={{ color: theme.text.secondary }}
                >
                  Téléchargez vos fichiers LiDAR pour les intégrer à votre
                  projet
                </p>
              </div>
              <X
                className="transform cursor-pointer transition duration-300 hover:scale-125"
                size={24}
                style={{ color: theme.text.secondary }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = theme.text.primary
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = theme.text.secondary
                }}
                onClick={handleCloseModal}
              />
            </div>

            <div
              className="data-layers-scroll flex-grow overflow-y-auto px-6 py-6"
              style={{
                maxHeight: 'calc(90vh - 200px)',
                minHeight: '200px',
              }}
            >
              <ImportProgressPanel
                progressState={progressState}
                uploadQueue={uploadQueue}
                maxQueue={maxQueue}
                effectiveUploading={effectiveUploading}
                onRemoveFromQueue={removeFromQueue}
              />

              <FileDropzone
                fileName={fileName}
                accept={FILE_ACCEPT}
                onFileSelect={handleFileSelect}
              />

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DateField
                  selectedDate={selectedDate}
                  selectedDateString={selectedDateString}
                  onChange={handleDateChange}
                />
                <div>
                  <label
                    className="text-sm font-medium"
                    style={{ color: theme.text.primary }}
                  >
                    Nom du fichier
                  </label>
                  <input
                    type="text"
                    name="fileName"
                    placeholder="Nom du fichier"
                    required
                    className="mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
                    style={{
                      borderColor: theme.input.border,
                      backgroundColor: theme.input.bg,
                      color: theme.input.text,
                    }}
                    onFocus={e => {
                      e.currentTarget.style.borderColor =
                        theme.input.borderFocus
                      e.currentTarget.style.backgroundColor =
                        theme.input.bgFocus
                    }}
                    onBlur={e => {
                      e.currentTarget.style.borderColor = theme.input.border
                      e.currentTarget.style.backgroundColor = theme.input.bg
                    }}
                    id="fileName"
                    value={fileName}
                    onChange={event => setFileName(event.target.value)}
                  />
                </div>
              </div>

              <RastersTable
                rasters={rasters}
                onDelete={setRasterToDelete}
                canDelete={canDeleteRaster}
              />
            </div>

            <Footer
              effectiveUploading={effectiveUploading}
              isQueueFull={isQueueFull}
              maxQueue={maxQueue}
              onCancel={handleCancel}
              onClose={handleCloseModal}
              onSubmit={handleSubmit}
            />

            {/* Portalise en z-[999999] : passe au-dessus de cette modale. */}
            <ConfirmModal
              type="warning"
              isOpen={!!rasterToDelete}
              title="Supprimer le raster"
              message={`Supprimer « ${rasterToDelete?.filename ?? ''} » ? Cette action est définitive.`}
              confirmText="Supprimer définitivement"
              cancelText="Annuler"
              onConfirm={confirmDeleteRaster}
              onCancel={() => setRasterToDelete(null)}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ModalLidar
